import {
  Group,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentWorkspace,
  useWorkspaceAsset
} from '@gripp/shared-logic';
import {
  Colors,
  LoadingIndicator,
  useSlideLayout
} from '@gripp/shared-ui';
import { MultiItemSelectorModal } from 'libs/shared-ui/src/components/forms/multiItemSelectorModal';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

type GroupItem = { id: string; name: string };

const EditGroup = ({
  assetId,
  selectedGroups,
  onGroupsChange,
  resetRef,
}: {
  assetId?: string;
  selectedGroups: Array<GroupItem>;
  onGroupsChange: (groups: Array<GroupItem>) => void;
  resetRef?: React.RefObject<(() => void) | undefined>;
}) => {
  const { t } = useTranslation();
  const { setColumnTitle } = useSlideLayout();

  const { workspaceAsset, isLoading } = useWorkspaceAsset(
    assetId as string
  );
  const { createGroup } = useCreateGroup();
  
  const currentWorkspace = useCurrentWorkspace();
  const { groups: availableGroups, isLoading: isGroupsLoading } = useAllWorkspaceGroups(
    workspaceAsset?.workspace?.id ?? currentWorkspace?.id
  );

  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const allGroups = useMemo(() => {
    const combined = [...availableGroups, ...selectedGroups];
    const map = new Map<string, Group>();
    combined.forEach((group) => {
      if (group?.id) {
        map.set(group.id, { id: group.id, name: group.name });
      }
    });
    return Array.from(map.values());
  }, [selectedGroups, availableGroups]);

  const selectedGroupIds = useMemo(
    () => new Set(selectedGroups.map((group) => group.id)),
    [selectedGroups]
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const base = query
      ? allGroups.filter((group) => group.name.toLowerCase().includes(query))
      : allGroups;

    return base.map((group) => ({
      id: group.id,
      name: group.name,
      isActive: selectedGroupIds.has(group.id),
    }));
  }, [allGroups, search, selectedGroupIds]);


  const canCreateItem =
    !!search.trim() &&
    !allGroups.some(
      (group) => group.name.toLowerCase() === search.trim().toLowerCase()
    );

  const handleItemToggle = (item: Group) => {
    const isSelected = selectedGroupIds.has(item.id);
    if (isSelected) {
      onGroupsChange(selectedGroups.filter((group) => group.id !== item.id));
      return;
    }
    onGroupsChange([...selectedGroups, { id: item.id, name: item.name }]);
  };

  const handleToggleAll = (selectAll: boolean) => {
    if (selectAll) {
      const toAdd = filteredItems
        .filter((item) => !selectedGroupIds.has(item.id))
        .map((item) => ({ id: item.id, name: item.name }));
      onGroupsChange([...selectedGroups, ...toAdd]);
    } else {
      const filteredIds = new Set(filteredItems.map((item) => item.id));
      onGroupsChange(selectedGroups.filter((g) => !filteredIds.has(g.id)));
    }
  };

  const handleCreateGroup = async (name: string): Promise<Group> => {
    const created = await createGroup(name);
    return {
      id: created.id,
      name: created.name,
    };
  };

  const handleCreateItem = async () => {
    if (!search.trim()) {
      return;
    }

    setIsCreating(true);
    try {
      const newGroup = await handleCreateGroup(search.trim());
      onGroupsChange([...selectedGroups, newGroup]);
      setSearch('');
    } finally {
      setIsCreating(false);
    }
  };

  const handleReset = () => {
    onGroupsChange(
      workspaceAsset?.groups?.map((g) => ({ id: g.id, name: g.name })) ?? []
    );
    setSearch('');
  };

  if (resetRef) {
    resetRef.current = handleReset;
  }

  useEffect(() => {
    setColumnTitle(`${t('asset.groups')} (${selectedGroups.length})`);
  }, [setColumnTitle, t, selectedGroups.length]);


  if (isGroupsLoading) {
    return (
      <LoadingIndicator />
    );
  }

  return (
    <View style={editGroupStyles.container}>
      <MultiItemSelectorModal
        showOptions={true}
        search={search}
        setSearch={setSearch}
        searchPlaceholder={t('groups.common.selectGroup')}
        filteredItems={filteredItems}
        onItemToggle={handleItemToggle}
        onToggleAll={handleToggleAll}
        canCreateItem={canCreateItem}
        onCreateItem={handleCreateGroup}
        onCreatePress={handleCreateItem}
        isCreating={isCreating}
        createButtonText={t('common.create')}
      />
    </View>
  );
};

const editGroupStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  toolbar: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 4,
  },
  resetButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.grayDivider,
  },
  resetButtonText: {
    fontSize: 14,
    color: Colors.black,
  },
});

export default EditGroup;